import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modern/components/ui/alert-dialog";
import { updateURLQueryParam } from "@/other/utils/urlUtils";

const TIMEOUT = 10000; // 10 seconds
const TIMESTAMP_KEY = "timestamp";
const QUERY_PARAM_KEY = "_cb";
const VERSION_URL = "/timestamp.json";
const MAX_RETRIES = 3;
const CHECK_INTERVAL = 1000 * 60; // 1 minute
const BROADCAST_CHANNEL_NAME = "swivu-cache-buster";
const BROADCAST_ACTION_RELOAD = "RELOAD";

type CacheBusterBroadcastMessage = {
  action: typeof BROADCAST_ACTION_RELOAD;
  version: string;
};

interface CacheBusterProps {
  isAppLoaded: boolean;
}

const changeFavicon = (url: string): void => {
  let link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
};

export const CacheBuster: React.FC<CacheBusterProps> = ({ isAppLoaded }) => {
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const isCheckingRef = useRef(false);
  const retryCountRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTimeStampRef = useRef<string | null>(
    localStorage.getItem(TIMESTAMP_KEY)
  );
  const dismissedTimeStampRef = useRef<string | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const clearCaches = async (): Promise<void> => {
    try {
      console.info("[CacheBuster] Clearing caches...");
      // Clear service workers
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
        console.info("[CacheBuster] Service workers cleared");
      }
      // Clear browser caches
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
      console.info("[CacheBuster] Storage cleared");
    } catch (error) {
      console.error("[CacheBuster] Cache clearing failed:", error);
      throw error;
    }
  };

  const performReload = async (
    version: string,
    options: { broadcast: boolean }
  ): Promise<void> => {
    try {
      changeFavicon("/swivu-grey.ico");
      localStorage.setItem(TIMESTAMP_KEY, JSON.stringify(version));
      if (options.broadcast && broadcastChannelRef.current) {
        const message: CacheBusterBroadcastMessage = {
          action: BROADCAST_ACTION_RELOAD,
          version,
        };
        broadcastChannelRef.current.postMessage(message);
      }
      await clearCaches();
      // Hard reload to bypass all caches
      window.location.href =
        window.location.href.split("?")[0] + `?${QUERY_PARAM_KEY}=${version}`;
    } catch (error) {
      console.error("[CacheBuster] Reload failed:", error);
    }
  };

  const handleReload = async (): Promise<void> => {
    if (!newVersion) return;
    await performReload(newVersion, { broadcast: true });
  };

  const handleDismiss = (): void => {
    if (newVersion) {
      dismissedTimeStampRef.current = newVersion;
    }
    setNewVersion(null);
  };

  const checkVersion = async (): Promise<void> => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (!data[TIMESTAMP_KEY]) {
        throw new Error("Invalid version response: missing version field");
      }
      retryCountRef.current = 0;
      const remoteVersion = data[TIMESTAMP_KEY] as string;
      let storedVersion = currentTimeStampRef.current;
      if (!storedVersion) {
        const latestTimeStamp = JSON.stringify(remoteVersion);
        localStorage.setItem(TIMESTAMP_KEY, latestTimeStamp);
        currentTimeStampRef.current = latestTimeStamp;
        return;
      }
      storedVersion = JSON.parse(storedVersion);
      // Check for version mismatch
      const isNewVersion =
        remoteVersion !== (dismissedTimeStampRef.current || storedVersion);
      if (isNewVersion) {
        changeFavicon("/swivu-grey.ico");
        setNewVersion(remoteVersion);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.warn("[CacheBuster] Version check failed:", err.message);
      // Implement exponential backoff retry
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        const retryDelay = Math.min(
          5000 * Math.pow(2, retryCountRef.current - 1),
          CHECK_INTERVAL / 2
        );
        console.info(
          `[CacheBuster] Scheduling retry ${retryCountRef.current}/${MAX_RETRIES} in ${retryDelay}ms`
        );
        setTimeout(() => {
          isCheckingRef.current = false;
          checkVersion();
        }, retryDelay);
      } else {
        console.warn(
          "[CacheBuster] Max retries reached, will try again on next interval"
        );
        retryCountRef.current = 0;
      }
    } finally {
      // If retries mechanism is triggered then this condition will prevent extra calls from interval
      if (retryCountRef.current === 0) {
        isCheckingRef.current = false;
      }
    }
  };

  useEffect(() => {
    if (isAppLoaded) {
      // If the app is loaded, we can safely remove the cache buster query param
      updateURLQueryParam(QUERY_PARAM_KEY, undefined, true);
    }
  }, [isAppLoaded]);

  useEffect(() => {
    checkVersion();
    intervalRef.current = setInterval(checkVersion, CHECK_INTERVAL);

    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      broadcastChannelRef.current = channel;

      const onBroadcastMessage = (event: MessageEvent): void => {
        const data = event.data as Partial<CacheBusterBroadcastMessage>;
        if (data?.action === BROADCAST_ACTION_RELOAD && Boolean(data.version)) {
          void performReload(data.version as string, { broadcast: false });
        }
      };

      channel.addEventListener("message", onBroadcastMessage);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isCheckingRef.current = false;
      channel?.close();
      broadcastChannelRef.current = null;
    };
  }, []);

  return (
    <AlertDialog open={!!newVersion} onOpenChange={(open) => !open && setNewVersion(null)}>
      <AlertDialogContent className={"z-[--swivu-bryntum-z-index] !pointer-events-auto"} >
        <AlertDialogHeader>
          <AlertDialogTitle>Swivu Update Available</AlertDialogTitle>
          <AlertDialogDescription>
            A new version of the application is available. Do you want to reload
            now?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleDismiss}>Close</AlertDialogCancel>
          <AlertDialogAction onClick={handleReload}>Reload</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
